import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Crown, Zap, Infinity as InfIcon, Trophy, Layers, Lock, Sparkles, Heart, Palette, Wind, CalendarClock, Tag, X } from "lucide-react";
import { startCheckout, refreshProStatus, getEmail, setEmail as saveEmail, setIsPro, validateCoupon } from "@/lib/pro";
import { sfx } from "@/lib/sfx";
import { useT } from "@/i18n";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaywallDialog({ open, onOpenChange, feature, onUnlocked, defaultTab = "pro" }) {
  const { t } = useT();
  const featureLabel = feature || t("paywall.default_feature");
  const [email, setEmail] = useState(getEmail() || "");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("buy"); // "buy" | "restore"
  const [tab, setTab] = useState(defaultTab); // "pro" | "season"
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // { id, percent_off, amount_off, expires_at? }
  const [couponBusy, setCouponBusy] = useState(false);
  const [countdown, setCountdown] = useState("");
  const [copyVariant, setCopyVariant] = useState(null); // A/B copy for auto-applied coupons

  // Sync tab when dialog re-opens with a different defaultTab prop
  React.useEffect(() => {
    if (open) setTab(defaultTab);
  }, [open, defaultTab]);

  // Auto-apply first-buy / referral discount when the dialog first opens
  React.useEffect(() => {
    if (!open || couponApplied) return;
    // Precedence: referral > first-buy welcome
    let auto = null;
    let addTimer = false;
    try {
      const refCoupon = localStorage.getItem("ppe_referral_coupon");
      if (refCoupon) auto = refCoupon;
      else if (!localStorage.getItem("ppe_first_buy_seen") && !getEmail()) {
        auto = "WELCOME10";
        addTimer = true;
      }
    } catch {}
    if (!auto) return;
    (async () => {
      try {
        const info = await validateCoupon(auto);
        const expiresAt = addTimer ? Date.now() + 10 * 60 * 1000 : null; // 10-min urgency window
        setCouponApplied({ ...info, code: auto, expires_at: expiresAt });
        setCoupon(auto);
        // Fetch A/B copy variant for auto-applied welcome coupons + log impression
        try {
          const res = await axios.get(`${API}/coupon-copy/active?code=${encodeURIComponent(auto)}`);
          const v = res?.data?.variant;
          if (v) {
            setCopyVariant(v);
            axios.post(`${API}/coupon-copy/event`, {
              code: auto,
              variant: v.id || "default",
              action: "impression",
            }).catch(() => {});
          }
        } catch {}
        try {
          if (auto === "WELCOME10") {
            localStorage.setItem("ppe_first_buy_seen", "1");
            if (expiresAt) localStorage.setItem("ppe_welcome_expires_at", String(expiresAt));
          }
        } catch {}
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Countdown timer for welcome-coupon urgency
  React.useEffect(() => {
    if (!couponApplied?.expires_at) { setCountdown(""); return; }
    const tick = () => {
      const ms = couponApplied.expires_at - Date.now();
      if (ms <= 0) {
        setCouponApplied(null);
        setCoupon("");
        try { localStorage.removeItem("ppe_welcome_expires_at"); } catch {}
        toast.info(t("paywall.welcome_expired") || "Welcome discount expired");
        return;
      }
      const s = Math.floor(ms / 1000);
      setCountdown(`${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponApplied?.expires_at]);

  const applyCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true);
    try {
      const info = await validateCoupon(code);
      setCouponApplied({ ...info, code });
      toast.success(t("paywall.coupon_applied", { code }) || `${code} applied — ${info.percent_off ? info.percent_off + "% OFF" : "$" + (info.amount_off/100).toFixed(2) + " OFF"}`);
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 404 ? (t("paywall.coupon_not_found") || "Coupon not found") : (t("paywall.coupon_invalid") || "Invalid coupon"));
    } finally {
      setCouponBusy(false);
    }
  };

  const clearCoupon = () => {
    setCoupon("");
    setCouponApplied(null);
  };

  const buy = async (lookup_key) => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      toast.error(t("paywall.toast_invalid_email"));
      return;
    }
    setBusy(true);
    try {
      saveEmail(e);
      // Log A/B copy conversion (fire-and-forget) BEFORE the redirect
      if (copyVariant && couponApplied?.code) {
        axios.post(`${API}/coupon-copy/event`, {
          code: couponApplied.code,
          variant: copyVariant.id || "default",
          action: "conversion",
        }).catch(() => {});
      }
      const { checkout_url } = await startCheckout({ lookup_key, email: e, coupon_code: couponApplied?.code });
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(t("paywall.toast_checkout_failed"));
      setBusy(false);
    }
  };

  const restore = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      toast.error(t("paywall.toast_purchase_email"));
      return;
    }
    setBusy(true);
    try {
      const res = await refreshProStatus(e);
      if (res.is_pro || res.is_season) {
        setIsPro(!!res.is_pro);
        toast.success(t("paywall.toast_pro_unlocked"));
        onOpenChange(false);
        onUnlocked && onUnlocked();
      } else {
        toast.error(t("paywall.toast_no_pro_found"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="paywall-dialog"
        className="max-w-3xl bg-black/80 backdrop-blur-2xl border border-white/10 text-white rounded-none p-0"
      >
        {/* Tab switch */}
        <div className="flex border-b border-white/10">
          <button
            data-testid="paywall-tab-pro"
            onClick={() => { sfx.click(); setTab("pro"); }}
            className="flex-1 px-4 py-3 text-[10px] tracking-[0.3em] transition-colors flex items-center justify-center gap-2"
            style={{
              color: tab === "pro" ? "#FFD400" : "rgba(255,255,255,0.55)",
              borderBottom: tab === "pro" ? "2px solid #FFD400" : "2px solid transparent",
              background: tab === "pro" ? "rgba(255,212,0,0.06)" : "transparent",
            }}
          >
            <Crown size={12} /> {t("paywall.tab_pro") || "PRO"}
          </button>
          <button
            data-testid="paywall-tab-season"
            onClick={() => { sfx.click(); setTab("season"); }}
            className="flex-1 px-4 py-3 text-[10px] tracking-[0.3em] transition-colors flex items-center justify-center gap-2"
            style={{
              color: tab === "season" ? "#F0ABFC" : "rgba(255,255,255,0.55)",
              borderBottom: tab === "season" ? "2px solid #F0ABFC" : "2px solid transparent",
              background: tab === "season" ? "rgba(240,171,252,0.06)" : "transparent",
            }}
          >
            <Sparkles size={12} /> {t("paywall.tab_season") || "SEASON PASS"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5">
          {/* Left: pitch */}
          <div className="md:col-span-3 p-8 border-r border-white/10 bg-gradient-to-br from-yellow-500/5 to-transparent">
            <DialogHeader className="text-left space-y-2">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.4em]" style={{ color: tab === "season" ? "#F0ABFC" : "#FFD400" }}>
                {tab === "season" ? <><Sparkles size={14} /> {t("paywall.season_tag") || "LIMITED SEASON"}</> : <><Crown size={14} /> {t("paywall.tag")}</>}
              </div>
              <DialogTitle className="font-display text-3xl md:text-4xl tracking-tighter text-white">
                {tab === "season" ? (
                  <>{t("paywall.season_title_lead") || "UNLEASH"} <span style={{ color: "#F0ABFC", textShadow: "0 0 20px rgba(240,171,252,0.5)" }}>{t("paywall.season_title_accent") || "THE SEASON"}</span></>
                ) : (
                  <>{t("paywall.title_lead")} <span className="text-[#FFD400]" style={{ textShadow: "0 0 20px rgba(255,212,0,0.5)" }}>{t("paywall.title_accent")}</span></>
                )}
              </DialogTitle>
              <DialogDescription className="text-white/60 pt-1">
                {tab === "season"
                  ? (t("paywall.season_subtitle") || "Time-limited perks that stack with Pro.")
                  : `${featureLabel} ${t("paywall.subtitle")}`}
              </DialogDescription>
            </DialogHeader>

            {copyVariant && couponApplied && (
              <div
                data-testid="coupon-ab-banner"
                className="mt-4 border border-[#39FF14] bg-[#39FF14]/5 px-4 py-3"
                style={{ boxShadow: "0 0 20px rgba(57,255,20,0.15)" }}
              >
                <div className="text-sm font-bold text-[#39FF14] tracking-wide">{copyVariant.headline}</div>
                <div className="text-[11px] text-white/70 mt-1 tracking-wide">{copyVariant.subtitle}</div>
              </div>
            )}

            {tab === "pro" ? (
              <ul className="mt-6 space-y-3 text-sm">
                <Row icon={<Layers size={14} />} accent="#00F0FF" label={t("paywall.bullet_3d")} />
                <Row icon={<InfIcon size={14} />} accent="#FF0055" label={t("paywall.bullet_stages")} />
                <Row icon={<Zap size={14} />} accent="#39FF14" label={t("paywall.bullet_powerups")} />
                <Row icon={<Trophy size={14} />} accent="#FFD400" label={t("paywall.bullet_leaderboard")} />
              </ul>
            ) : (
              <ul className="mt-6 space-y-3 text-sm">
                <Row icon={<Heart size={14} />} accent="#FF0055" label={t("paywall.season_perk_life") || "+1 starting life every run"} />
                <Row icon={<Zap size={14} />} accent="#F0ABFC" label={t("paywall.season_perk_xp") || "2× score & XP multiplier"} />
                <Row icon={<Wind size={14} />} accent="#00F0FF" label={t("paywall.season_perk_level") || "Bonus level · Neon Rain"} />
                <Row icon={<Palette size={14} />} accent="#F0ABFC" label={t("paywall.season_perk_skin") || "Exclusive Season Champion skin"} />
                <Row icon={<CalendarClock size={14} />} accent="#FFD400" label={t("paywall.season_perk_pro") || "Includes all Pro levels while active"} />
              </ul>
            )}

            <div className="mt-6 text-[10px] tracking-[0.3em] text-white/40">
              {t("paywall.test_mode")} <span className="text-white/70">4242 4242 4242 4242</span> {t("paywall.test_card_hint")}
            </div>
          </div>

          {/* Right: purchase panel */}
          <div className="md:col-span-2 p-8 flex flex-col gap-4">
            <div>
              <label className="text-[10px] tracking-[0.3em] text-white/50 block mb-2">{t("paywall.email_label")}</label>
              <Input
                data-testid="paywall-email-input"
                type="email"
                placeholder={t("paywall.email_ph")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#FFD400] font-mono lowercase"
              />
              <div className="text-[10px] text-white/40 mt-1 tracking-wider">
                {t("paywall.email_help")}
              </div>
            </div>

            {/* Coupon field */}
            <div data-testid="coupon-block">
              <label className="text-[10px] tracking-[0.3em] text-white/50 block mb-2 flex items-center gap-2"><Tag size={11} /> {t("paywall.coupon_label") || "COUPON"}</label>
              {couponApplied ? (
                <div className="flex items-center justify-between px-3 py-2 border border-[#39FF14] bg-[#39FF14]/5" data-testid="coupon-applied">
                  <div className="text-xs tracking-widest text-[#39FF14] font-mono">
                    {couponApplied.code} · {couponApplied.percent_off ? `${couponApplied.percent_off}% OFF` : `$${(couponApplied.amount_off/100).toFixed(2)} OFF`}
                    {countdown && (
                      <span data-testid="coupon-countdown" className="ml-2 text-[#FFD400] font-bold">
                        ⏱ {countdown}
                      </span>
                    )}
                  </div>
                  <button
                    data-testid="coupon-clear"
                    onClick={clearCoupon}
                    className="text-white/60 hover:text-[#FF0055] transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    data-testid="coupon-input"
                    type="text"
                    placeholder={t("paywall.coupon_ph") || "PROMO25"}
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && coupon && applyCoupon()}
                    className="bg-transparent border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#39FF14] font-mono uppercase text-xs"
                  />
                  <button
                    data-testid="coupon-apply"
                    disabled={couponBusy || !coupon}
                    onClick={applyCoupon}
                    className="px-3 py-1 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40"
                  >
                    {t("paywall.coupon_apply") || "APPLY"}
                  </button>
                </div>
              )}
            </div>

            {mode === "buy" ? (
              tab === "pro" ? (
                <div className="flex flex-col gap-3">
                  <button
                    data-testid="buy-monthly-button"
                    disabled={busy}
                    onClick={() => { sfx.click(); buy("pro_monthly"); }}
                    className="px-5 py-4 border border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors disabled:opacity-40 text-left"
                  >
                    <div className="text-xs tracking-[0.3em] font-semibold">{t("paywall.monthly")}</div>
                    <div className="text-3xl font-bold font-mono">$4.99<span className="text-sm opacity-70">{t("paywall.per_month")}</span></div>
                    <div className="text-[10px] tracking-widest opacity-80">{t("paywall.monthly_sub")}</div>
                  </button>
                  <button
                    data-testid="restore-toggle"
                    onClick={() => setMode("restore")}
                    className="text-[10px] tracking-[0.3em] text-white/50 hover:text-white text-left mt-2 flex items-center gap-2"
                  >
                    <Lock size={12} /> {t("paywall.restore_toggle")}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <button
                    data-testid="buy-season-daily-button"
                    disabled={busy}
                    onClick={() => { sfx.click(); buy("season_daily"); }}
                    className="px-5 py-3 border border-white/40 text-white hover:bg-white hover:text-black transition-colors disabled:opacity-40 text-left"
                  >
                    <div className="text-xs tracking-[0.3em] font-semibold">{t("paywall.season_daily") || "24-HOUR PASS"}</div>
                    <div className="text-2xl font-bold font-mono">$1.99</div>
                    <div className="text-[10px] tracking-widest opacity-80">{t("paywall.season_daily_sub") || "ONE DAY OF PERKS"}</div>
                  </button>
                  <button
                    data-testid="buy-season-monthly-button"
                    disabled={busy}
                    onClick={() => { sfx.click(); buy("season_monthly"); }}
                    className="px-5 py-4 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors disabled:opacity-40 text-left"
                    style={{ boxShadow: "0 0 24px rgba(240,171,252,0.35)" }}
                  >
                    <div className="text-xs tracking-[0.3em] font-semibold">{t("paywall.season_monthly") || "SEASON MONTHLY"} <span className="ml-1 opacity-70">· {t("paywall.best_value") || "BEST VALUE"}</span></div>
                    <div className="text-3xl font-bold font-mono">$4.99<span className="text-sm opacity-70">{t("paywall.per_month")}</span></div>
                    <div className="text-[10px] tracking-widest opacity-80">{t("paywall.season_monthly_sub") || "PERKS ACTIVE AS LONG AS YOU RENEW"}</div>
                  </button>
                  <button
                    data-testid="buy-season-30day-button"
                    disabled={busy}
                    onClick={() => { sfx.click(); buy("season_30day"); }}
                    className="px-5 py-3 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black transition-colors disabled:opacity-40 text-left"
                  >
                    <div className="text-xs tracking-[0.3em] font-semibold">{t("paywall.season_30day") || "30-DAY PASS"}</div>
                    <div className="text-2xl font-bold font-mono">$9.99</div>
                    <div className="text-[10px] tracking-widest opacity-80">{t("paywall.season_30day_sub") || "ONE-TIME · 30 DAYS OF PERKS"}</div>
                  </button>
                  <button
                    data-testid="restore-toggle"
                    onClick={() => setMode("restore")}
                    className="text-[10px] tracking-[0.3em] text-white/50 hover:text-white text-left mt-2 flex items-center gap-2"
                  >
                    <Lock size={12} /> {t("paywall.restore_toggle")}
                  </button>
                </div>
              )
            ) : (
              <div className="flex flex-col gap-3">
                <div className="text-xs text-white/70">
                  {t("paywall.restore_hint")}
                </div>
                <button
                  data-testid="restore-button"
                  disabled={busy}
                  onClick={() => { sfx.click(); restore(); }}
                  className="px-5 py-3 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors disabled:opacity-40 text-xs tracking-[0.3em]"
                >
                  {t("paywall.restore_button")}
                </button>
                <button
                  onClick={() => setMode("buy")}
                  className="text-[10px] tracking-[0.3em] text-white/50 hover:text-white text-left"
                >
                  {t("paywall.back_purchase")}
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon, accent, label }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 w-6 h-6 flex items-center justify-center border" style={{ borderColor: accent, color: accent }}>
        {icon}
      </span>
      <span className="text-white/85">{label}</span>
    </li>
  );
}
